import { motion, useReducedMotion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import { Button, InstallSnippet } from '@mdxui/primitives'
import type { HeroMediaGridProps } from './types'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  heroHeadlineVariants,
  heroDescriptionVariants,
  heroButtonsVariants,
  heroMediaVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'
import { CodeWindowHero } from '../code-window'

const HeroMediaGrid = ({
  title = 'Build Beautiful Interfaces with MDXUI',
  subtitle = 'Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.',
  callToAction,
  callToActionUrl,
  secondaryCallToAction,
  secondaryCallToActionUrl,
  installSnippet,
  primaryImage = { src: '/placeholders/placeholder-1.svg', alt: 'Hero image' },
  primaryMediaType = 'code',
  primaryCodeWindow = {
    code: `import { Hero } from '@mdxui/neo'

export default function Page() {
  return (
    <Hero
      variant="image-grid"
      title="Build Beautiful Interfaces"
      callToAction="Get Started"
    />
  )
}`,
    language: 'tsx',
    filename: 'page.tsx',
  },
  secondaryImage = { src: '/placeholders/placeholder-1.svg', alt: 'Secondary image', ctaText: 'Learn more', ctaUrl: '#' },
  secondaryMediaType = 'image',
  secondaryCodeWindow,
  codeTheme = 'dark',
  codeShowCopy = false,
  background,
  behindNavbar = true,
  className,
}: Omit<HeroMediaGridProps, 'variant'>) => {
  const prefersReducedMotion = useReducedMotion()

  const headlineAnimation = prefersReducedMotion ? reducedMotionVariants : heroHeadlineVariants
  const descAnimation = prefersReducedMotion ? reducedMotionVariants : heroDescriptionVariants
  const buttonsAnimation = prefersReducedMotion ? reducedMotionVariants : heroButtonsVariants
  const mediaAnimation = prefersReducedMotion ? reducedMotionVariants : heroMediaVariants

  return (
    <div className={cn(
      'relative overflow-hidden',
      behindNavbar && BEHIND_NAVBAR_CLASSES,
      className
    )}>
      {background && (
        <div className="absolute inset-0">{background}</div>
      )}
      <motion.section
        className="relative z-10 py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container mx-auto">
        <div className="mx-auto flex max-w-5xl flex-col items-center">
          <div className="z-10 flex flex-col items-center gap-8 text-center">
            <div className="max-w-3xl">
              <motion.h1
                variants={headlineAnimation}
                className="mb-4 text-4xl font-semibold text-pretty lg:text-6xl"
              >
                {title}
              </motion.h1>
              {subtitle && (
                <motion.div variants={descAnimation}>
                  <Markdown size="lg">{subtitle}</Markdown>
                </motion.div>
              )}
            </div>
            {(callToAction || secondaryCallToAction || installSnippet) && (
              <motion.div
                variants={buttonsAnimation}
                className="flex w-full flex-col justify-center gap-2 sm:flex-row"
              >
                {installSnippet ? (
                  <InstallSnippet
                    package={installSnippet.package}
                    command={installSnippet.command}
                    className="w-full sm:w-auto"
                  />
                ) : (
                  <>
                    {callToAction && callToActionUrl && (
                      <Button asChild className="w-full sm:w-auto">
                        <a href={callToActionUrl}>
                          {callToAction}
                          <ChevronRight className="h-4" />
                        </a>
                      </Button>
                    )}
                    {secondaryCallToAction && secondaryCallToActionUrl && (
                      <Button asChild variant="ghost" className="w-full sm:w-auto">
                        <a href={secondaryCallToActionUrl}>
                          {secondaryCallToAction}
                        </a>
                      </Button>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </div>
        </div>
        <motion.div
          variants={mediaAnimation}
          className="mx-auto mt-20 grid grid-cols-1 max-w-7xl gap-px bg-border p-px md:grid-cols-5"
        >
          {primaryMediaType === 'code' && primaryCodeWindow ? (
            <CodeWindowHero
              code={primaryCodeWindow.code}
              language={primaryCodeWindow.language}
              filename={primaryCodeWindow.filename}
              showHeader={false}
              showCopy={codeShowCopy}
              style="borderless"
              theme={codeTheme}
              size="fit"
              className="w-full md:col-span-3 md:min-h-[500px] md:max-h-[500px]"
            />
          ) : (
            <img
              src={primaryImage.src}
              alt={primaryImage.alt}
              className="h-full max-h-[500px] w-full object-cover md:col-span-3"
            />
          )}
          <div className="relative md:col-span-2">
            {secondaryMediaType === 'code' && secondaryCodeWindow ? (
              <CodeWindowHero
                code={secondaryCodeWindow.code}
                language={secondaryCodeWindow.language}
                filename={secondaryCodeWindow.filename}
                showHeader={false}
                showCopy={codeShowCopy}
                style="borderless"
                theme={codeTheme}
                size="fit"
                className="w-full md:min-h-[500px] md:max-h-[500px]"
              />
            ) : (
              <img
                src={secondaryImage.src}
                alt={secondaryImage.alt}
                className="h-full max-h-[500px] w-full object-cover"
              />
            )}
            {secondaryMediaType !== 'code' && secondaryImage.ctaText && (
              <Button asChild variant="ghost" className="absolute right-5 bottom-5">
                <a href={secondaryImage.ctaUrl || '#'}>
                  {secondaryImage.ctaText}
                  <ChevronRight className="h-4" />
                </a>
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </motion.section>
    </div>
  )
}

export { HeroMediaGrid }
